// REACT
import React, { useState, createRef, useEffect, useMemo, useRef } from 'react';
import clsx from 'clsx';

// OTHER
import { makeStyles } from '@material-ui/core/styles';

//MATERIAL UI
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TablePagination from '@material-ui/core/TablePagination';
import TableRow from '@material-ui/core/TableRow';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import Paper from '@material-ui/core/Paper';
import Grid from '@material-ui/core/Grid';
import Icon from '@material-ui/core/Icon';

const useStyles = makeStyles((theme) => ({
  tableRoot: {
    '& .MuiTableCell-stickyHeader': {
      backgroundColor: theme.palette.type === 'dark' ? '#424242' : '#fff',
    },
  },
  table: {
    minWidth: 750,
  },
  tableContainer: {
    maxHeight: 500,
    '&::-webkit-scrollbar': {
      display: 'none',
      width: 0,
      height: 0,
    },
    '& > *': {
      '& ::-webkit-scrollbar': {
        display: 'none',
        width: 0,
        height: 0,
      },
    },
    '-ms-overflow-style': 'none',
  },
  tableContainerMin: {
    minHeight: 500,
  },
  tableImg: {
    maxHeight: theme.spacing(5),
  },
  tableImgContainer: {},
  indexButton: {
    borderRadius: '50%',
    border: 'solid 1px #0ab7ff',
    textAlign: 'center',
    '&:hover': {
      backgroundColor: 'rgba(10, 183, 255, .25)',
    },
    '& .MuiIcon-colorAction': {
      color: '#0ab7ff',
    },
  },
  indexCell: {
    padding: '4px 8px 4px 8px',
    '-ms-overflow-style': 'none',
    '&::-webkit-scrollbar': {
      '-ms-overflow-style': 'none',
      display: 'none',
      width: 0,
      height: 0,
    },
  },
}));

const desc = (a, b, orderBy) => {
  let val1, val2;
  a.cell.map((cell) => {
    if (cell.cellIsDate) {
      cell.cellValue && (cell.cellValue = new Date(cell.cellValue));
    }
    if (cell.cellKey === orderBy) {
      val1 = cell.cellValue;
    }
  });
  b.cell.map((cell) => {
    if (cell.cellIsDate) {
      cell.cellValue && (cell.cellValue = new Date(cell.cellValue));
    }
    if (cell.cellKey === orderBy) {
      val2 = cell.cellValue;
    }
  });
  if (!val2 || !val1) {
    // HANDLE UNDEFINED SORT
    val1 = val1 ? 1 : 0;
    val2 = val2 ? 1 : 0;
    return val2 < val1 ? -1 : val2 > val1 ? 1 : 0;
  }
  val1 = val1 ? val1 : 0;
  val2 = val2 ? val2 : 0;
  return val2 < val1 ? -1 : val2 > val1 ? 1 : 0;
};

const stableSort = (array, cmp) => {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = cmp(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
};

const getSorting = (order, orderBy) => {
  return order === 'desc' ? (a, b) => desc(a, b, orderBy) : (a, b) => -desc(a, b, orderBy);
};

interface ITableComponent {
  dataSource: ITableRowKeys[];
  tableHeader: TableHeaderCellType[];
  className?: any;
  whenToHide?: boolean; //parent ref of when to hide; IE breakpoint = md
  handleRowClick?: any;
  indexed?: boolean;
  childComponent?: any;
  rowComponent?: any;
  preventReset?: boolean;
  colProps?: any; //TODO
  overRiderSort?: any;
  indexLinked?: boolean;
  sortCallback?: (value: boolean | number | string, value2: boolean | number | string) => void;
  rowPerPageCallback?: (value: boolean | number | string) => void;
  pageCallback?: (value: boolean | number | string) => void;
  param?: any;
  showEmptyRow?: boolean;
  noFooter?: boolean;
}

interface ITableRowKeys {
  // ROW
  isLink?: string;
  isLinkValue?: string;
  rowClassName?: string;
  rowData?: any;
  rowKey?: any;
  // CELL
  cell: TableCellType[];
}
type TableCellType = {
  cellKey: string;
  cellValue: any;
  cellClassName?: string;
  align?: 'left' | 'right';
  cellIsImage: boolean;
  cellIsDate?: boolean;
  cellCanHide?: boolean;
  cellIsComponent?: boolean;
  cellProps?: any | null;
};

type TableHeaderCellType = {
  keyName: string;
  cellValue: string;
  align: 'left' | 'right' | undefined;
  hideCell?: boolean;
  keyIsDate?: boolean;
  sort?: boolean;
  cellClassName?: string;
};

interface ICreateCellRow {
  dataSource: any[];
  searchedDataSource?: any[];
  cellKeys: string[];
  dateKeys?: string[];
  canHideKeys?: string[];
  linkIdKey?: string;
  linkValue?: string;
  countOnly?: any[];
  numberOnly?: string[];
  imageKeys?: string[];
  classes?: any;
  classCallBack?: any;
  rowClassCondition?: {
    key: string;
    value: (value: boolean | number | string) => void;
    className: any;
  }[];
  cellClassCondition?: {
    className: any;
    value: (value: boolean | number | string) => void;
    key: string;
  }[];
  yesNoOnly?: string[];
  alignRight?: string[];
  componentKey?: string[];
  rowKey?: string;
  cellKeyWithProps?: any;
  booleanKey?: string[];
}

export const TableComponent = (props: Partial<ITableComponent>) => {
  const {
    tableHeader,
    dataSource,
    className,
    whenToHide,
    handleRowClick,
    indexed,
    childComponent,
    rowComponent,
    colProps,
    indexLinked,
    overRiderSort,
    sortCallback,
    pageCallback,
    rowPerPageCallback,
    showEmptyRow,
    param,
    noFooter,
  } = props;
  // TODO TS
  const dataRow: any = dataSource;
  const headerRow: any = tableHeader;
  const combineClass = { ...useStyles(), ...className };
  const classes: any = combineClass;
  const [order, setOrder] = useState(param?.order || 'desc');
  const [orderBy, setOrderBy] = useState(param?.orderBy || '');
  const [page, setPage] = useState(param?.page || 0);
  const [rowsPerPage, setRowsPerPage] = useState(
    param?.rowsPerPage ? param.rowsPerPage : dataRow?.length > 15 ? 50 : 15
  );
  const firstRowRef: any = createRef();
  const tableRef: any = useRef();
  const [emptyRowHeight, setEmptyRowHeight] = useState(33);
  useEffect(() => {
    if (sortCallback) {
      sortCallback(orderBy, order);
    }
  }, [order, orderBy]);

  useEffect(() => {
    if (pageCallback) {
      pageCallback(page);
    }
  }, [page]);

  useEffect(() => {
    if (firstRowRef.current?.offsetHeight > 0) {
      setEmptyRowHeight(firstRowRef.current.offsetHeight);
    }
  }, [firstRowRef]);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    let table = tableRef.current;
    if (table) {
      table.scrollIntoView();
    }
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    if (rowPerPageCallback) {
      rowPerPageCallback(event.target.value);
    }
  };

  const handleLink = (id, value?) => {
    if (value) {
      handleRowClick(id, value);
    } else {
      handleRowClick(id);
    }
  };
  const createCell = (row) =>
    row.cell.map((cell: any, i) => {
      let PassInComponent: any;
      if (childComponent) {
        childComponent.map((c) => {
          if (c.cellKey === cell.cellKey) {
            PassInComponent = c.component({
              ...c.props,
              value: c?.keyValue ? row?.rowData?.item[c?.keyValue] : row?.rowData?.item,
              id: row.rowKey,
            });
          }
        });
      }
      return cell.cellIsComponent ? (
        <TableCell key={i} className={cell.cellClassName} {...cell.cellProps}>
          {PassInComponent}
        </TableCell>
      ) : !cell.cellIsImage ? (
        <TableCell
          key={i}
          className={cell.cellClassName}
          align={cell.align}
          hidden={cell.cellCanHide && whenToHide}
          {...cell.cellProps}
        >
          {cell.cellIsDate && cell.cellValue
            ? new Date(cell.cellValue).toLocaleDateString()
            : cell.cellValue
            ? cell.cellValue
            : ' '}
        </TableCell>
      ) : (
        <TableCell
          key={i}
          className={clsx(classes.tableImgContainer, cell.cellClassName)}
          {...cell.cellProps}
        >
          <img className={classes.tableImg} src={`${cell?.cellValue}`} />
        </TableCell>
      );
    });

  const emptyRows = rowsPerPage - Math.min(rowsPerPage, dataRow?.length - page * rowsPerPage);
  return (
    <Grid item xs={12}>
      <Paper className={classes.tableRoot}>
        <TableContainer
          className={clsx(classes.tableContainer, !showEmptyRow && classes.tableContainerMin)}
        >
          <Table className={classes.table} size='small' stickyHeader ref={tableRef}>
            {colProps && colProps.length > 0 && (
              <colgroup>
                {colProps.map((col, i) => (
                  <col key={i} {...col} />
                ))}
              </colgroup>
            )}
            <EnhancedTableHead
              classes={classes}
              order={order}
              orderBy={orderBy}
              onRequestSort={overRiderSort ? overRiderSort : handleRequestSort}
              rowCount={dataSource?.length}
              headCells={tableHeader}
              whenToHide={whenToHide}
            />
            <TableBody>
              {stableSort(dataSource, getSorting(order, orderBy))
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row, index) => {
                  if (!rowComponent) {
                    const tableCells = createCell(row);
                    return (
                      <ClickRow
                        hover
                        ref={index === 0 ? firstRowRef : null}
                        tabIndex={-1}
                        key={index}
                        className={row.rowClassName}
                        handleLink={
                          row.isLink && row.isLinkValue && !indexLinked
                            ? (e: any) => handleLink(row.isLink, row.isLinkValue)
                            : row.isLink && !indexLinked
                            ? (e: any) => handleLink(row.isLink)
                            : null
                        }
                      >
                        {indexed && indexLinked ? (
                          <TableCell className={classes.indexCell}>
                            <div
                              className={classes.indexButton}
                              onClick={() => handleLink(row.isLink)}
                            >
                              <Icon fontSize='inherit' color='action'>
                                {page === 0 ? index + 1 : page * rowsPerPage + index + 1}
                              </Icon>
                            </div>
                          </TableCell>
                        ) : (
                          indexed &&
                          !indexLinked && (
                            <TableCell className={classes.indexCell}>
                              {page === 0 ? index + 1 : page * rowsPerPage + index + 1}
                            </TableCell>
                          )
                        )}
                        {tableCells}
                      </ClickRow>
                    );
                  } else {
                    const tableCells = rowComponent.createCell
                      ? createCell(row[rowComponent.key])
                      : rowComponent.component;
                    return <></>;
                  }
                })}
              {showEmptyRow &&
                emptyRows > 0 &&
                [...Array(emptyRows)].map((e, i) => (
                  <TableRow key={i} style={{ height: emptyRowHeight }}>
                    <TableCell colSpan={headerRow.length + 1} />
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
        {!noFooter && (
          <TablePagination
            rowsPerPageOptions={
              param?.rowsPerPage
                ? [15, 50, 100, param.rowsPerPage].filter(
                    (a, b) => [15, 50, 100, param.rowsPerPage].indexOf(a) === b
                  )
                : [15, 50, 100]
            }
            component='div'
            count={dataRow?.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onChangePage={handleChangePage}
            onChangeRowsPerPage={handleChangeRowsPerPage}
          />
        )}
      </Paper>
    </Grid>
  );
};

// ---------- TABLE HEADER ----------

// const headCells = [
//     { id: 'shortName', align: false, disablePadding: false, label: 'Short Name', nameClass: 'cellName', hide: false },
//     { id: 'name', align: false, disablePadding: false, label: 'Name', nameClass: 'cellName', hide: false },
//     { id: 'collections', align: false, disablePadding: false, label: '# Videos', nameClass: 'cellDate', hide: true },
// ];

const EnhancedTableHead = (props) => {
  const { classes, order, orderBy, onRequestSort, headCells, whenToHide } = props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };
  return (
    <TableHead>
      <TableRow className={classes.row}>
        {headCells.map((headCell) =>
          headCell.cellIsSortable ? (
            <TableCell
              className={clsx(classes.cell, headCell.cellClassName)}
              key={headCell.keyName}
              align={headCell.align}
              sortDirection={orderBy === headCell.keyName ? order : false}
              hidden={headCell.canHideKey && whenToHide}
            >
              <TableSortLabel
                active={orderBy === headCell.keyName}
                direction={orderBy === headCell.keyName ? order : 'asc'}
                onClick={createSortHandler(headCell.keyName)}
              >
                {headCell.cellLabel}
              </TableSortLabel>
            </TableCell>
          ) : (
            <TableCell
              className={clsx(classes.cell, headCell.cellClassName)}
              key={headCell.keyName}
              align={headCell.align}
              hidden={headCell.canHideKey && whenToHide}
            >
              {headCell.cellLabel}
            </TableCell>
          )
        )}
      </TableRow>
    </TableHead>
  );
};

// ---------- END TABLE HEADER ----------

// ---------- Clickable Table Row ----------

const ClickRow = React.forwardRef((props: any, ref) => {
  // Destructure props to keep the expected MUI TableRow props
  // while having access to the rowData prop
  const { handleLink, ...restProps } = props;
  return (
    <TableRow ref={ref ? ref : null} {...restProps} onMouseDown={handleLink && handleLink}>
      {props.children}
    </TableRow>
  );
});

// ---------- END Clickable Table Row ----------

export const createCellRows = (props: ICreateCellRow) => {
  const {
    dataSource,
    searchedDataSource,
    cellKeys,
    alignRight,
    dateKeys,
    canHideKeys,
    classes,
    countOnly,
    numberOnly,
    imageKeys,
    linkIdKey,
    linkValue,
    classCallBack,
    rowClassCondition,
    cellClassCondition,
    componentKey,
    rowKey,
    cellKeyWithProps,
    yesNoOnly,
    booleanKey,
  } = props;
  let list = searchedDataSource ? searchedDataSource : dataSource;
  let result = list?.map((item: any, i) => {
    let rowKeyValue;
    let rowCells = cellKeys.map((cellKey) => {
      let cellClassName: any;
      cellClassCondition?.map((condition: any) => {
        const { key, value, className } = condition;
        if (cellKey === key) {
          if (value(item[key])) {
            cellClassName = className;
          }
        }
      });
      let returnValue: TableCellType;
      let checkClassName =
        classCallBack && !cellClassName
          ? classCallBack(cellKey)
          : classCallBack && cellClassName
          ? classCallBack(cellClassName)
          : null;
      let returnCellValue: any = '';
      if (item[cellKey]) {
        returnCellValue = item[cellKey];
      }
      if (countOnly && countOnly.filter((key) => key === cellKey).length > 0) {
        returnCellValue = item[cellKey].length;
      }
      if (numberOnly && numberOnly.filter((key) => key === cellKey).length > 0) {
        if (item[cellKey] === 0) {
          returnCellValue = '0';
        }
      }
      if (typeof item[cellKey] === typeof true) {
        let booleanValue: any = new Boolean(item[cellKey]);
        booleanValue = booleanValue.toString();
        booleanValue = booleanValue.charAt(0).toUpperCase() + booleanValue.slice(1);
        returnCellValue = booleanValue;
      }
      if (yesNoOnly && yesNoOnly.filter((key) => key === cellKey).length > 0) {
        if (booleanKey && booleanKey.filter((key) => key === cellKey).length > 0) {
          returnCellValue = item[cellKey] === true ? 'Y' : 'N';
        } else {
          returnCellValue = item[cellKey] ? 'Y' : 'N';
        }
      }
      returnValue = {
        cellKey: cellKey,
        cellValue: returnCellValue,
        cellClassName: checkClassName,
        cellIsImage:
          imageKeys && imageKeys.filter((key) => key === cellKey).length > 0 ? true : false,
        align:
          alignRight && alignRight.filter((key) => key === cellKey).length > 0 ? 'right' : 'left',
        cellIsDate: dateKeys && dateKeys.filter((key) => key === cellKey).length > 0 ? true : false,
        cellCanHide:
          canHideKeys && canHideKeys.filter((key) => key === cellKey).length > 0 ? true : false,
        cellIsComponent:
          componentKey && componentKey.filter((key) => key === cellKey).length > 0 ? true : false,
        cellProps:
          cellKeyWithProps && cellKeyWithProps.filter((key) => key.key === cellKey).length > 0
            ? cellKeyWithProps.filter((key) => key.key === cellKey)[0].props
            : null,
      };
      return returnValue;
    });

    // table connect base on cells / isLink / check to add className / data
    let haveRowClassCheck;
    rowClassCondition?.map((condition: any) => {
      const { key, value, className } = condition;
      if (value(item[key])) {
        haveRowClassCheck = className;
      }
    });
    let thereIsLink: string | undefined = undefined;
    if (linkIdKey) {
      thereIsLink = item[linkIdKey];
    }
    let thereIsLinkValue: string | undefined = undefined;
    if (linkValue) {
      thereIsLinkValue = item[linkValue];
    }
    if (rowKey) {
      rowKeyValue = item[rowKey];
    }
    let newReturn: ITableRowKeys = {
      isLink: thereIsLink,
      isLinkValue: thereIsLinkValue,
      // TableRow on click
      rowClassName: haveRowClassCheck ? classCallBack(haveRowClassCheck) : null,
      // <TableRow className={rowClassName}
      cell: rowCells,
      rowData: {
        item: item,
        // ref item object
      },
      rowKey: rowKeyValue,
    };
    return newReturn;
  });
  return result;
};

export const createCellHeader = (props) => {
  const { cellKeys, cellLabel, alignRight, dateKey, sortKey, hideKey, classCallBack } = props;
  return cellKeys.map((key, i) => {
    let checkAlign = false;
    alignRight?.map((alignKey) => {
      if (key === alignKey) {
        checkAlign = true;
      }
    });
    let isDate = false;
    dateKey?.map((dateKey) => {
      if (key === dateKey) {
        isDate = true;
      }
    });
    let isSortable = false;
    sortKey?.map((sortableKey) => {
      if (key === sortableKey) {
        isSortable = true;
      }
    });
    let hide = false;
    hideKey?.map((canHideKey) => {
      if (key === canHideKey) {
        hide = true;
      }
    });
    let checkClassName = classCallBack ? classCallBack(key) : null;
    return {
      keyName: key,
      cellLabel: cellLabel[i],
      align: checkAlign ? 'right' : 'left',
      cellIsDate: isDate ? true : false,
      cellIsSortable: isSortable ? true : false,
      cellCanHide: hide ? true : false,
      cellClassName: checkClassName,
    };
  });
};

export const useMemoizedTable = (tableComponent, list) => useMemo(() => tableComponent, [list]);

export const reducerParameter = (
  list,
  orderBy?: string,
  callbacks?: {
    sortCall?: any;
    rowPerPageCall?: any;
    pageCall?: any;
  },
  multipleTable?: number
) => {
  const sortCall = callbacks ? callbacks.sortCall : null;
  const rowPerPageCall = callbacks ? callbacks.rowPerPageCall : null;
  const pageCall = callbacks ? callbacks.pageCall : null;
  const numTable = multipleTable ? multipleTable : undefined;
  if (!numTable) {
    return {
      rowPerPageCallback: (number) => {
        if (!rowPerPageCall) {
          list.setControl({
            rowsPerPage: number,
          });
        } else {
          list.setControl({
            rowsPerPage: number,
          });
          rowPerPageCall(number);
        }
      },
      sortCallback: (orderBy, order) => {
        if (!sortCall) {
          list.setControl({
            orderBy: orderBy,
            order: order,
          });
        } else {
          list.setControl({
            orderBy: orderBy,
            order: order,
          });
          sortCall(orderBy, order);
        }
      },
      pageCallback: (page) => {
        if (!pageCall) {
          list.setControl({
            page: page,
          });
        } else {
          list.setControl({
            page: page,
          });
          pageCall(page);
        }
      },
      param: {
        rowsPerPage: list?.param?.rowsPerPage || 50,
        orderBy: list?.param?.orderBy || orderBy,
        order: list?.param?.order || 'desc',
        page: list?.param?.page || 0,
      },
    };
  } else {
    const rowsPerPageKey = `rowsPerPage` + numTable;
    const orderByKey = `orderBy` + numTable;
    const orderKey = `order` + numTable;
    const pageKey = `page` + numTable;
    return {
      rowPerPageCallback: (number) => {
        if (!rowPerPageCall) {
          list.setControl({
            [rowsPerPageKey]: number,
          });
        } else {
          list.setControl({
            [rowsPerPageKey]: number,
          });
          rowPerPageCall(number);
        }
      },
      sortCallback: (orderBy, order) => {
        if (!sortCall) {
          list.setControl({
            [orderByKey]: orderBy,
            [orderKey]: order,
          });
        } else {
          list.setControl({
            [orderByKey]: orderBy,
            [orderKey]: order,
          });
          sortCall(orderBy, order);
        }
      },
      pageCallback: (page) => {
        if (!pageCall) {
          list.setControl({
            [pageKey]: page,
          });
        } else {
          list.setControl({
            [pageKey]: page,
          });
          pageCall(page);
        }
      },
      param: {
        rowsPerPage: (list?.param && list?.param[rowsPerPageKey]) || 50,
        orderBy: (list?.param && list?.param[orderByKey]) || orderBy,
        order: (list?.param && list?.param[orderKey]) || 'desc',
        page: (list?.param && list?.param[pageKey]) || 0,
      },
    };
  }
};
